import React, { useEffect, useRef, useState, ReactNode } from "react";
import { FaFacebookF, FaSpotify, FaSquareXTwitter, FaGlobe } from "react-icons/fa6";
import { useLanguageContext } from "../context/LanguageContext";
import ShareButtons from "../components/ShareButtons";
import { fetchMockDraft, MockPick, ChatMessage } from "../services/mockDraftSheet";
import {
  convertHeightToMeters,
  convertPoundsToKilograms,
  translateExperienceToHeb,
} from "../utils";

// ----- Localization helpers (toggled by frontend, no per-cell duplication) -----

// Drafters' names: the sheet stores Hebrew; map to English on toggle.
const NAME_MAP: Record<string, string> = {
  "סמי": "Samy",
  "מודי": "Moody",
  "יונתן": "Jonathan",
  "דרור": "Dror",
  "מתי": "Mati",
};
function localizeName(name: string, isHE: boolean): string {
  const n = (name || "").trim();
  return isHE ? n : NAME_MAP[n] || n;
}

// Drafter profile cards shown on hover. Placeholder content — hardcode later.
// Keyed by English name so a pick/comment author resolves regardless of language.
// fullName / details are authored [Hebrew, English]; localized via pickLang().
export interface Drafter {
  fullName: string[];
  details: string[];
  links: string[];
}
const draftersArray: Record<string, Drafter> = {
  Samy: {
    fullName: ["סמי זליקסון", "Samy Zelikson"],
    details: ["מאמן כדורסל נערים בישראל וסקאוטר.", "Youth basketball coach and  scout in Israel."],
    links: [""],
  },
  Moody: {
    fullName: ["מודי רבפוגל", "Moody Rabfogel"],
    details: [
      "מורה דרך בפראג, כותב באתר הופס על ליגת המכללות ומגיש של הפודקאסטים ׳אזהרת מסע׳ על טיולים למקומות אסורים, ו׳שחקני קצה׳ על הליגה הטובה בעולם.",
      "A tour guide in Prague who covers college basketball for Hoops.co.il and hosts the podcasts 'Travel Warning,' on trips to off-limits places, and 'Fringe Players,' on the best league in the world.",
    ],
    links: ["https://open.spotify.com/show/2HGqNEWdXHqbPqNvxBzg46?si=a2c203bc703d431b"],
  },
  Jonathan: {
    fullName: ["יונתן גזלה", "Jonathan Gazala"],
    details: [
      "עובד בחברת פרויקטים גדולה מול משרד החינוך, אוהד הסופרסוניקס בעבר וחובב NBA בהווה. כותב המון שנים על הדראפט באתר הופס, אבל קודם כל אוהד NBA ורק תייר בכדורסל המכללות. ה-NBA תמיד הנחה אותי בניתוח הדראפט והמגמות בליגה זו המשקפת היחידה דרכה אני בוחן פרוספקטים.",
      "Works at a large projects firm partnering with Israel's Ministry of Education; a former SuperSonics fan and a present-day NBA devotee. I've written about the draft for Hoops.co.il for many years, but first and foremost an NBA fan and only a tourist in college basketball — the NBA and its trends have always guided his draft analysis, and this is the single lens through which he evaluates prospects.",
    ],
    links: ["https://hoops.co.il/?p=341806"],
  },
  Dror: {
    fullName: ["דרור האס", "Dror Haas"],
    details: [
      "אוהד הסלטיקס, צופה באינספור משחקי כדורסל בשביל הפרנסה ומתפעל את אתר ׳הכדור הכתום׳ בשביל הנשמה.",
      "A Celtics fan who watches countless basketball games for a living and runs the 'Orange Ball' site for the love of it.",
    ],
    links: [
      "https://orangeball.co.il/author/drorhaas/",
      "https://www.facebook.com/NBAorangeball/",
      "https://x.com/orangeballil",
    ],
  },
  Mati: {
    fullName: ["מתי בארי", "Mati Be'eri"],
    details: [
      "אוהד הטרייל בלייזרס מתקופת סבוניס והלאה, עכבר דראפט מזה שני עשורים, ומייסד האתר הופס בורדינג גייט.",
      "A Trail Blazers fan since the Sabonis era, a draft junkie for two decades, and founder of Hoops Boarding Gate.",
    ],
    links: [
      "https://x.com/Hoops_BG",
      "https://www.facebook.com/people/Hoops-Boarding-Gate-%D7%94%D7%95%D7%A4%D7%A1-%D7%91%D7%95%D7%A8%D7%93%D7%99%D7%A0%D7%92-%D7%92%D7%99%D7%99%D7%98/61584076406945/",
    ],
  },
};
function getDrafter(rawName: string): Drafter | undefined {
  const n = (rawName || "").trim();
  return draftersArray[NAME_MAP[n] || n];
}

// Positions: write the English token on the sheet (PG/SG/SF/PF/C/G/F).
const POSITION_MAP: Record<string, string> = {
  PG: "פוינט גארד",
  SG: "שוטינג גארד",
  SF: "סמול פורוורד",
  PF: "פאוור פורוורד",
  C: "סנטר",
  G: "גארד",
  F: "פורוורד",
  Big: "ביגמן",
  Wing: "ווינג"
};
function localizePosition(pos: string, isHE: boolean): string {
  const p = (pos || "").trim();
  if (!p) return p;
  // Bilingual cell ("English | Hebrew") wins, e.g. "Big | ביגמן".
  if (p.includes("|")) return localized(p, isHE, false);
  if (!isHE) return p;
  const key = Object.keys(POSITION_MAP).find((k) => k.toLowerCase() === p.toLowerCase());
  return key ? POSITION_MAP[key] : p;
}

// Experience: write the English class on the sheet; utils maps to Hebrew.
function localizeExperience(exp: string, isHE: boolean): string {
  const e = (exp || "").trim();
  if (!isHE || !e) return e;
  return translateExperienceToHeb(e) || e;
}

// Free-text bilingual fields, written as two halves separated by "|".
// Reasoning & comments are authored "Hebrew | English" (heFirst).
// Player names & college/teams are authored "English | Hebrew" (heFirst=false).
// Cells without a "|" show that single version in both languages.
function localized(value: string, isHE: boolean, heFirst = true): string {
  const parts = (value || "").split("|").map((s) => s.trim());
  if (parts.length < 2) return parts[0] || "";
  const he = heFirst ? parts[0] : parts[1];
  const en = heFirst ? parts[1] : parts[0];
  return (isHE ? he : en) || parts[0];
}

// Measurements: write imperial on the sheet (e.g. 6'8 height/wingspan, 215 weight).
function formatHeight(value: string, isHE: boolean): string {
  const v = (value || "").trim();
  if (!v) return "";
  if (!isHE) return v.includes("'") ? `${v}"` : v;
  const m = convertHeightToMeters(v);
  return m === "נתון לא זמין" ? v : `${m} מ'`;
}
function formatWeight(value: string, isHE: boolean): string {
  const v = (value || "").trim();
  if (!v) return "";
  if (!isHE) return /lb/i.test(v) ? v : `${v} lbs`;
  const num = parseFloat(v);
  return isNaN(num) ? v : `${convertPoundsToKilograms(num)} ק"ג`;
}

// Consistent accent color per participant (keyed on the raw/stored name so it
// stays stable across language toggles).
const PALETTE = ["#F88158", "#5BA3F0", "#5FCF8E", "#E06A9C", "#B58BE8", "#E0B341"];
// Fixed, distinct colors for the known drafters (keyed by canonical English name)
// so no two collide. Unknown names fall back to a stable hash.
const NAME_COLORS: Record<string, string> = {
  Samy: "#F88158",
  Moody: "#5BA3F0",
  Jonathan: "#5FCF8E",
  Dror: "#E06A9C",
  Mati: "#B58BE8",
};
function colorFor(key: string): string {
  const canonical = NAME_MAP[(key || "").trim()] || (key || "").trim();
  if (NAME_COLORS[canonical]) return NAME_COLORS[canonical];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

const Avatar: React.FC<{ label: string; colorKey: string; size?: number }> = ({
  label,
  colorKey,
  size = 38,
}) => (
  <span
    className="inline-flex shrink-0 items-center justify-center rounded-full font-bold text-white shadow-elevation"
    style={{ width: size, height: size, backgroundColor: colorFor(colorKey), fontSize: size * 0.42 }}
    aria-hidden="true"
  >
    {(label || "?").trim().charAt(0)}
  </span>
);

// Renders a link as a brand/website icon based on its URL.
const DrafterLink: React.FC<{ url: string }> = ({ url }) => {
  const u = url.toLowerCase();
  let Icon = FaGlobe;
  if (u.includes("facebook")) Icon = FaFacebookF;
  else if (u.includes("spotify")) Icon = FaSpotify;
  else if (u.includes("x.com") || u.includes("twitter")) Icon = FaSquareXTwitter;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-gray-200 transition hover:bg-white/20 hover:text-white"
    >
      <Icon size={15} />
    </a>
  );
};

// Hover card for a drafter, attached to their name and avatar.
const DrafterHover: React.FC<{
  drafter?: Drafter;
  displayName: string;
  isHE: boolean;
  children: ReactNode;
}> = ({ drafter, displayName, isHE, children }) => {
  const [open, setOpen] = useState(false);
  const [anchorTop, setAnchorTop] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLSpanElement | null>(null);

  // Touch devices fire no mouseleave — close the card on a tap outside it.
  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDocPointer);
    return () => document.removeEventListener("pointerdown", onDocPointer);
  }, [open]);

  if (!drafter) return <>{children}</>;

  const show = () => {
    if (timer.current) clearTimeout(timer.current);
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) setAnchorTop(rect.bottom);
    setOpen(true);
  };
  const hide = () => {
    timer.current = setTimeout(() => setOpen(false), 150);
  };

  // [Hebrew, English]; English is the default, fall back to whichever exists.
  const pickLang = (arr: string[]) => ((isHE ? arr?.[0] : arr?.[1] || arr?.[0]) || "").trim();
  const name = pickLang(drafter.fullName) || displayName;
  const details = pickLang(drafter.details);
  const links = (drafter.links || []).filter((l) => l && l.trim() !== "");

  return (
    <span
      ref={rootRef}
      className="relative inline-block cursor-pointer"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={show}
      tabIndex={0}
    >
      {children}
      {open && (
        <span
          dir={isHE ? "rtl" : "ltr"}
          onMouseEnter={show}
          onMouseLeave={hide}
          style={{ top: anchorTop + 8 }}
          className="fixed left-1/2 z-50 block w-64 max-w-[calc(100vw-1.5rem)] -translate-x-1/2"
        >
          {/* Animation lives on this inner element so the fade-in's translateY
              never overrides the outer -translate-x-1/2 centering. */}
          <span className="block rounded-xl border border-white/10 bg-[#23272F] p-3 text-start shadow-elevation-lg ring-1 ring-white/10 animate-fade-in">
            <span className="block text-sm font-bold text-white">{name}</span>
            {details && (
              <span className="mt-1 block text-xs leading-relaxed text-gray-300">{details}</span>
            )}
            {links.length > 0 && (
              <span className="mt-2 flex flex-wrap gap-2">
                {links.map((url, i) => (
                  <DrafterLink key={i} url={url} />
                ))}
              </span>
            )}
          </span>
        </span>
      )}
    </span>
  );
};

const BioChips: React.FC<{ pick: MockPick; isHE: boolean }> = ({ pick, isHE }) => {
  const items: Array<[string, string]> = [
    [isHE ? "גובה" : "Height", formatHeight(pick.height, isHE)],
    [isHE ? "ווינגספאן" : "Wingspan", formatHeight(pick.wingspan, isHE)],
    [isHE ? "משקל" : "Weight", formatWeight(pick.weight, isHE)],
    [isHE ? "גיל" : "Age", (pick.age || "").trim()],
  ].filter(([, v]) => v !== "") as Array<[string, string]>;

  if (items.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {items.map(([label, value]) => (
        <span
          key={label}
          className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2.5 py-1 text-xs text-gray-300 ring-1 ring-white/10"
        >
          <span className="text-gray-400">{label}</span>
          <span className="font-medium text-gray-100">{value}</span>
        </span>
      ))}
    </div>
  );
};

const ResponseBubble: React.FC<{ msg: ChatMessage; isHE: boolean }> = ({ msg, isHE }) => (
  <div className="flex items-start gap-2.5">
    <DrafterHover drafter={getDrafter(msg.author)} displayName={localizeName(msg.author, isHE)} isHE={isHE}>
      <Avatar label={localizeName(msg.author, isHE)} colorKey={msg.author} size={30} />
    </DrafterHover>
    <div className="flex-1 rounded-2xl rounded-ss-sm bg-white/5 px-3.5 py-2.5 ring-1 ring-white/10">
      {msg.author && (
        <DrafterHover drafter={getDrafter(msg.author)} displayName={localizeName(msg.author, isHE)} isHE={isHE}>
          <span className="mb-0.5 inline-block text-xs font-bold" style={{ color: colorFor(msg.author) }}>
            {localizeName(msg.author, isHE)}
          </span>
        </DrafterHover>
      )}
      <p className="whitespace-pre-wrap text-start text-sm leading-relaxed text-gray-200" dir="auto">
        {localized(msg.text, isHE)}
      </p>
    </div>
  </div>
);

const PickThread: React.FC<{ pick: MockPick; isHE: boolean }> = ({ pick, isHE }) => {
  const [open, setOpen] = useState(false);
  const hasResponses = pick.responses.length > 0;

  const playerName = localized(pick.player, isHE, false);
  const descriptor = [
    localizePosition(pick.position, isHE),
    localized(pick.teamCollege, isHE, false),
    localizeExperience(pick.experience, isHE),
  ]
    .filter((s) => s && s.trim() !== "")
    .join(", ");

  const linkClass =
    "font-bold text-[#F88158] underline decoration-[#F88158]/40 decoration-2 underline-offset-4 transition-colors hover:text-[#FFB088] hover:decoration-[#F88158]";

  const playerEl = pick.profileLink ? (
    <a href={pick.profileLink} target="_blank" rel="noopener noreferrer" className={linkClass}>
      {playerName}
      <span className="ms-0.5 align-top text-xs opacity-60">{isHE ? "↖" : "↗"}</span>
    </a>
  ) : (
    <span className="font-bold" style={{ color: "#F88158" }}>
      {playerName}
    </span>
  );

  return (
    <section className="mb-6">
      {/* Pick / team divider */}
      <div className="mb-3 flex items-center gap-3">
        <div className="h-px flex-1 bg-white/10" />
        <span className="rounded-full bg-white/5 px-3 py-1 text-xs font-medium text-gray-300 ring-1 ring-white/10">
          {(isHE ? "בחירה " : "Pick ") + (pick.pick || "—")} · {pick.team}
        </span>
        <div className="h-px flex-1 bg-white/10" />
      </div>

      {/* Picker message */}
      <div className="flex items-start gap-2.5">
        <DrafterHover drafter={getDrafter(pick.picker)} displayName={localizeName(pick.picker, isHE)} isHE={isHE}>
          <Avatar label={localizeName(pick.picker, isHE)} colorKey={pick.picker} />
        </DrafterHover>
        <div className="flex-1 rounded-2xl rounded-ss-sm bg-[#21252E] px-4 py-3 shadow-elevation ring-1 ring-white/10">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <DrafterHover drafter={getDrafter(pick.picker)} displayName={localizeName(pick.picker, isHE)} isHE={isHE}>
              <span className="text-sm font-bold" style={{ color: colorFor(pick.picker) }}>
                {localizeName(pick.picker, isHE) || (isHE ? "בוחר" : "GM")}
              </span>
            </DrafterHover>
            <span className="text-xs text-gray-400">{isHE ? "בוחר ב:" : "selected:"}</span>
          </div>

          {playerName && (
            <div className="mt-1 text-start text-lg leading-snug" dir="auto">
              {playerEl}
              {descriptor && (
                <span className="text-sm font-normal text-gray-400">, {descriptor}</span>
              )}
            </div>
          )}

          <BioChips pick={pick} isHE={isHE} />

          {pick.reasoning && (
            <p className="mt-3 whitespace-pre-wrap text-start text-sm leading-relaxed text-gray-200" dir="auto">
              {localized(pick.reasoning, isHE)}
            </p>
          )}

          {hasResponses && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs font-medium text-gray-300 ring-1 ring-white/10 transition hover:bg-white/10 hover:text-white active:scale-95"
            >
              {open
                ? isHE
                  ? "הסתר תגובות"
                  : "Hide replies"
                : (isHE ? "תגובות" : "Replies") + ` (${pick.responses.length})`}
            </button>
          )}
        </div>
      </div>

      {/* Replies */}
      {open && hasResponses && (
        <div className="mt-3 space-y-3 ps-6">
          {pick.responses.map((msg, i) => (
            <ResponseBubble key={i} msg={msg} isHE={isHE} />
          ))}
        </div>
      )}
    </section>
  );
};

const CollectiveMockDraftPage: React.FC = () => {
  const { isHE } = useLanguageContext();
  const [picks, setPicks] = useState<MockPick[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    fetchMockDraft()
      .then((data) => active && setPicks(data))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-terminal px-4 py-6" dir={isHE ? "rtl" : "ltr"}>
      <div className="mx-auto max-w-2xl">
        <ShareButtons
          isHE={isHE}
          photoURL={null}
          fixedFloat
          title={
            isHE
              ? "מוק דראפט קולקטיבי | Hoops Boarding Gate"
              : "Collective Mock Draft | Hoops Boarding Gate"
          }
          description={
            isHE
              ? "חילקנו 30 בחירות סיבוב ראשון, ואת 25 קבוצות ה-NBA שמיועדות לבצע אותן, בין חמישה חובבי דראפט ישראלים."
              : "We divided 30 first round picks, and the 25 NBA teams slotted to make them, among five Israeli draft junkies."
          }
        />
        <h1 className="text-start text-2xl font-bold" style={{ color: "#F88158" }}>
          {isHE ? "מוק דראפט קולקטיבי" : "Collective Mock Draft"}
        </h1>
        <p className="mt-1 text-start text-sm text-gray-400">
          {isHE
            ? "חילקנו 30 בחירות סיבוב ראשון, ואת 25 קבוצות ה-NBA שמיועדות לבצע אותן, בין חמישה חובבי דראפט ישראלים."
            : "We divided 30 first round picks, and the 25 NBA teams slotted to make them, among five Israeli draft junkies."}
        </p>
        <p className="mb-6 mt-0.5 text-start text-sm text-gray-400">
          {isHE
            ? "לחצו על שם השחקן כדי לקרוא את הדו״ח המלא עליו; רחפו מעל שם או האוואטר של הבוחר כדי לראות את הפרטים שלו."
            : "Click a player's name to read his full scouting report; hover a drafter's name or avatar to see their details."}
        </p>

        {error && (
          <div className="rounded-2xl border border-white/10 bg-[#191C22] p-5 text-sm text-gray-300">
            {isHE ? "טעינת הנתונים נכשלה. נסו לרענן." : "Couldn't load the draft. Try refreshing."}
          </div>
        )}

        {!error && picks === null && (
          <div className="text-sm text-gray-400">{isHE ? "טוען..." : "Loading…"}</div>
        )}

        {!error && picks !== null && picks.map((p, i) => <PickThread key={i} pick={p} isHE={isHE} />)}
      </div>
    </div>
  );
};

export default CollectiveMockDraftPage;
